type TuneTailorEvent =
  | "checkout_started"
  | "tunetailor_sample_played"
  | "tunetailor_cta_clicked";

export type AnalyticsConsent = "unknown" | "granted" | "denied";

const consentStorageKey = "tunetailor:analytics-consent";

const allowedFields: Record<TuneTailorEvent, ReadonlySet<string>> = {
  checkout_started: new Set(["songCount", "addOnCount", "catalog", "locale"]),
  tunetailor_sample_played: new Set(["track"]),
  tunetailor_cta_clicked: new Set(["target"]),
};

const analyticsUrl =
  import.meta.env.VITE_WORKER_ANALYTICS_URL ||
  "https://tunetailor-workflow.baneasa-new-concept.workers.dev/analytics";

export function analyticsConsent(): AnalyticsConsent {
  try {
    const value = localStorage.getItem(consentStorageKey);
    return value === "granted" || value === "denied" ? value : "unknown";
  } catch {
    return "unknown";
  }
}

export function setAnalyticsConsent(consent: Exclude<AnalyticsConsent, "unknown">): void {
  try {
    localStorage.setItem(consentStorageKey, consent);
  } catch {
    // Storage can be unavailable in private browsing or hardened environments.
  }
}

export function clearAnalyticsConsent(): void {
  try {
    localStorage.removeItem(consentStorageKey);
  } catch {
    // Storage can be unavailable in private browsing or hardened environments.
  }
}

export function trackTuneTailorEvent(
  name: TuneTailorEvent,
  data: Record<string, string | number | boolean | undefined> = {},
): void {
  if (analyticsConsent() !== "granted") {
    return;
  }

  const safeData = Object.fromEntries(
    Object.entries(data).filter(([key, value]) => allowedFields[name].has(key) && value !== undefined),
  );
  const payload = {
    name,
    id: typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}`,
    timestamp: new Date().toISOString(),
    shop: "tune-tailor.myshopify.com",
    data: safeData,
  };

  fetch(analyticsUrl, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {
    // Analytics should never block checkout or sample playback.
  });
}
